import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Extracts error message from blob response if request failed
 */
const parseBlobErrorMessage = async (err, defaultMsg) => {
  if (err.response && err.response.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const json = JSON.parse(text);
      if (json && json.detail) {
        return json.detail;
      }
    } catch (e) {
      // Not JSON
    }
  }
  return err.response?.data?.detail || defaultMsg;
};

/**
 * Downloads a document PDF by fetching it as a binary blob with credentials,
 * then creating a temporary download link in browser.
 */
export const downloadDocumentPdf = async (docId, defaultFilename = 'Appointment_Order.pdf') => {
  try {
    toast.info('Preparing PDF download...');
    const response = await axios.get(`${API_URL}/documents/issued/${docId}/pdf`, {
      responseType: 'blob',
      withCredentials: true,
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    // Parse filename from Content-Disposition header if provided
    let filename = defaultFilename;
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename=["']?([^"';]+)["']?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    toast.success(`Downloaded: ${filename}`);
  } catch (err) {
    console.error('PDF Download error:', err);
    const errMsg = await parseBlobErrorMessage(err, 'Failed to download PDF document.');
    toast.error(errMsg);
  }
};

/**
 * On-demand download helper for employee profile pages
 */
export const downloadEmployeeAppointmentOrderPdf = async (empId, defaultFilename = 'Appointment_Order.pdf') => {
  try {
    toast.info('Generating Official Letter PDF...');
    const response = await axios.get(`${API_URL}/documents/employees/${empId}/appointment-order/download`, {
      responseType: 'blob',
      withCredentials: true,
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    let filename = defaultFilename;
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename=["']?([^"';]+)["']?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    toast.success(`Downloaded: ${filename}`);
  } catch (err) {
    console.error('PDF Download error:', err);
    const errMsg = await parseBlobErrorMessage(err, 'Failed to generate or download Official Letter PDF');
    toast.error(errMsg);
  }
};
